import React, { useState, useRef, useEffect, useCallback } from "react";
import { MarkerState } from "../../../Context/MapMarkerContext/MapMarkerContext";
import "./CustomMap.css";

const GOOGLE_MAPS_API_KEY = "AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg";

let googleMapsLoading = false;
let googleMapsCallbacks = [];

function loadGoogleMaps(callback) {
  if (window.google && window.google.maps) {
    callback();
    return;
  }
  googleMapsCallbacks.push(callback);
  if (googleMapsLoading) return;
  googleMapsLoading = true;

  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
  script.async = true;
  script.defer = true;
  script.onload = () => {
    googleMapsLoading = false;
    googleMapsCallbacks.forEach((cb) => cb());
    googleMapsCallbacks = [];
  };
  document.head.appendChild(script);
}

const CustomMap = ({
  coordinates,
  showActionBox = false,
  showStatus = false,
  chooseCoordinates,
  zoom = 8,
  popUpShow,
}) => {
  const mapDivRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showToolBar, setShowToolBar] = useState(false);
  const { setMarkerId } = MarkerState();

  // Always keep a ref to the latest coordinates so initMap (called async
  // after the Google Maps script loads) reads the current value, not a
  // stale closure — this is the core fix for the edit-mode marker bug.
  const coordinatesRef = useRef(coordinates);
  useEffect(() => {
    coordinatesRef.current = coordinates;
  }, [coordinates]);

  const getCenter = (coords) => {
    if (coords && coords.length > 0) {
      return {
        lat: coords.reduce((acc, loc) => acc + loc.x, 0) / coords.length,
        lng: coords.reduce((acc, loc) => acc + loc.y, 0) / coords.length,
      };
    }
    return { lat: 34.1526, lng: 77.5771 };
  };

  // Place (or move) a single marker on the map
  const placeMarker = useCallback((map, lat, lng) => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    const marker = new window.google.maps.Marker({
      position: { lat, lng },
      map,
      icon: {
        path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
        fillColor: "#f44336",
        fillOpacity: 1,
        strokeColor: "#b71c1c",
        strokeWeight: 1,
        scale: 7,
        anchor: new window.google.maps.Point(0, 3),
      },
    });
    markersRef.current.push(marker);
    return marker;
  }, []);

  const initMap = useCallback(() => {
    if (!mapDivRef.current || mapInstanceRef.current) return;

    // Read the LATEST coordinates at init time via ref — fixes the stale
    // closure problem where the modal always opened at the default position
    // even when editing a facility that already had saved coordinates.
    const latestCoords = coordinatesRef.current;
    const center = getCenter(latestCoords);

    const map = new window.google.maps.Map(mapDivRef.current, {
      center,
      zoom,
      mapTypeId: "roadmap",
      disableDefaultUI: true,
      zoomControl: true,
      streetViewControl: false,
      fullscreenControl: false,
    });

    mapInstanceRef.current = map;

    // Edit mode: if valid coordinates already exist, show marker at saved position
    if (
      latestCoords &&
      latestCoords.length > 0 &&
      latestCoords[0].x &&
      latestCoords[0].y
    ) {
      placeMarker(map, latestCoords[0].x, latestCoords[0].y);
    }

    // Map click: place/move marker and fire the parent callback
    map.addListener("click", (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setShowToolBar(false);
      setMarkerId(0);
      if (chooseCoordinates) {
        placeMarker(map, lat, lng);
        chooseCoordinates(lng, lat);
      }
    });

    setIsLoaded(true);
  }, [zoom, chooseCoordinates, placeMarker]);

  // Load Google Maps script once on mount, then init the map
  useEffect(() => {
    loadGoogleMaps(() => {
      initMap();
    });
  }, []);

  // If coordinates prop changes AFTER the map is already initialised
  // (parent updates setCOOrdinates with saved lat/lng during edit populate),
  // sync the marker position and pan the map to match.
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded) return;
    if (
      coordinates &&
      coordinates.length > 0 &&
      coordinates[0].x &&
      coordinates[0].y
    ) {
      const lat = coordinates[0].x;
      const lng = coordinates[0].y;
      placeMarker(mapInstanceRef.current, lat, lng);
      mapInstanceRef.current.panTo({ lat, lng });
    }
  }, [coordinates, isLoaded, placeMarker]);

  // Sync zoom prop changes
  useEffect(() => {
    if (mapInstanceRef.current && isLoaded) {
      mapInstanceRef.current.setZoom(zoom);
    }
  }, [zoom, isLoaded]);

  return (
    <>
      {showToolBar && showActionBox}
      <div ref={mapDivRef} style={{ height: "100%", width: "100%" }} />
      {!isLoaded && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f0f0f0",
          }}
        >
          Loading map...
        </div>
      )}
    </>
  );
};

export default CustomMap;